<template>
  <div class="contract-upsert-v2">
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:toolbar>
            <div class="row">
              <div
                v-if="contract && checkPermissions(['CONTRACT_VIEW'])"
                class="ml-3"
              >
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="exportingDocx"
                  @click="handleExportDocx"
                >
                  <b-spinner v-if="exportingDocx" small class="mr-1" />
                  <i
                    v-else
                    style="font-size: 1rem"
                    class="far fa-file-word"
                  ></i>
                  <b>Xuất file Word</b>
                </b-button>
              </div>
              <div v-if="contract && canCancelContract" class="ml-3">
                <b-button
                  size="sm"
                  variant="danger"
                  @click="cancelDialog.show = true"
                >
                  <i style="font-size: 1rem" class="fas fa-ban"></i>
                  <b>Hủy HĐ</b>
                </b-button>
              </div>
              <div class="ml-3">
                <b-button size="sm" variant="secondary" @click="goBack">
                  <i style="font-size: 1rem" class="fas fa-arrow-left"></i>
                  <b>Quay lại</b>
                </b-button>
              </div>
            </div>
          </template>

          <template v-slot:preview>
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" />
              <p class="mt-3">Đang tải HĐ...</p>
            </div>

            <template v-else-if="!contract">
              <b-alert variant="danger" show>
                {{ loadError || 'Không tìm thấy HĐ' }}
              </b-alert>
            </template>

            <template v-else>
              <b-container class="bv-example-row">
                <!-- Header summary HĐ — dùng b-row + chip pattern legacy -->
                <b-row class="mb-3">
                  <b-col cols="3">
                    <b><i class="fas fa-hashtag mr-1"></i>Mã HĐ:</b>
                    <span class="ml-1">{{
                      contract.contractNumber || contract.id
                    }}</span>
                  </b-col>
                  <b-col cols="3">
                    <b>Trạng thái:</b>
                    <b-badge
                      class="ml-1"
                      :variant="statusVariant(currentStatus)"
                    >
                      {{ statusLabel(currentStatus) }}
                    </b-badge>
                  </b-col>
                  <b-col cols="3">
                    <b>Ngày HĐ:</b>
                    <span class="ml-1">{{
                      formatDate(contract.contractDate)
                    }}</span>
                  </b-col>
                  <b-col cols="3">
                    <b>Hình thức:</b>
                    <span class="ml-1">{{ contract.paymentType || '-' }}</span>
                  </b-col>
                </b-row>

                <!-- Link Đề nghị nguồn (nếu HĐ tạo từ Đề nghị) — show code mapping
                     từ BE leftJoinAndMapOne `contract.contractRequest` -->
                <b-row v-if="contract.contractRequestId" class="mb-3">
                  <b-col cols="12">
                    <b><i class="fas fa-link mr-1"></i>Đề nghị nguồn:</b>
                    <a
                      href="javascript:void(0)"
                      class="ml-1 text-primary"
                      @click="goToContractRequest(contract.contractRequestId)"
                    >
                      <span>{{
                        (contract.contractRequest &&
                          contract.contractRequest.code) ||
                        contract.contractRequestId
                      }}</span>
                      <i
                        class="fas fa-external-link-alt ml-1"
                        style="font-size: 11px"
                      ></i>
                    </a>
                  </b-col>
                </b-row>

                <!-- Section: 4 cột (ContractInfo + CustomerInfo + EmployeeInfo + Discount) read-only -->
                <b-row v-if="loadingComponent">
                  <b-col>
                    <ContractInfo
                      :contractData="contractData"
                      :action="'update'"
                      :isDisabled="true"
                    />
                  </b-col>
                  <b-col>
                    <CustomerInfo
                      :contractData="contractData"
                      :action="'update'"
                      :isDisabled="true"
                    />
                  </b-col>
                  <b-col>
                    <EmployeeInfo
                      :storeId="contractData.storeId"
                      :contractData="contractData"
                      :contractDataEmployeeInfo="contractEmployeeInfo"
                      :action="'update'"
                      :isDisabled="true"
                    />
                  </b-col>
                  <b-col>
                    <Discount
                      :contractData="contractData"
                      :action="'update'"
                      :isDisabled="true"
                    />
                  </b-col>
                </b-row>

                <!-- Customer snapshot extras (Sprint 3 specific — không có trong Customer entity) -->
                <hr
                  class="hr-text"
                  data-content="Snapshot KH (mở rộng)"
                  style="font-weight: 600"
                />
                <b-row>
                  <b-col cols="6" md="4">
                    <b>Email:</b>
                    <span class="ml-1">{{
                      contract.customerEmail || '-'
                    }}</span>
                  </b-col>
                  <b-col cols="6" md="4">
                    <b>Ghi chú KH:</b>
                    <span class="ml-1">{{ contract.customerNote || '-' }}</span>
                  </b-col>
                  <template v-if="contract.customerType === 'CUSTOMER'">
                    <b-col cols="6" md="4">
                      <b>CCCD:</b>
                      <span class="ml-1">{{
                        contract.customerIdCard || '-'
                      }}</span>
                    </b-col>
                    <b-col cols="6" md="4">
                      <b>Ngày cấp CCCD:</b>
                      <span class="ml-1">{{
                        formatDate(contract.customerIdCardIssueDate)
                      }}</span>
                    </b-col>
                    <b-col cols="6" md="4">
                      <b>Nơi cấp CCCD:</b>
                      <span class="ml-1">{{
                        contract.customerIdCardIssuePlace || '-'
                      }}</span>
                    </b-col>
                  </template>
                  <template v-else>
                    <b-col cols="6" md="4">
                      <b>Mã số thuế:</b>
                      <span class="ml-1">{{
                        contract.customerTaxNo || '-'
                      }}</span>
                    </b-col>
                    <b-col cols="6" md="4">
                      <b>Người đại diện:</b>
                      <span class="ml-1">{{
                        contract.customerRepresentative || '-'
                      }}</span>
                    </b-col>
                    <b-col cols="6" md="4">
                      <b>Chức danh:</b>
                      <span class="ml-1">{{
                        contract.customerRepPosition || '-'
                      }}</span>
                    </b-col>
                    <b-col cols="6" md="4">
                      <b>Ngày thành lập:</b>
                      <span class="ml-1">{{
                        formatDate(contract.customerFoundDate)
                      }}</span>
                    </b-col>
                  </template>
                </b-row>

                <!-- Section: Items read-only (reuse ProductTable disabled + button override) -->
                <hr
                  class="hr-text"
                  data-content="Thông tin sản phẩm"
                  style="font-weight: 600"
                />
                <ProductTable
                  v-if="loadingComponent"
                  :editable="false"
                  :isDisabled="true"
                  :contractData="contractData"
                />

                <!-- Override price action row — không trong ProductTable vì legacy không có -->
                <div
                  v-if="
                    loadingComponent && canOverridePrice && mainItems.length
                  "
                  class="mt-2"
                >
                  <small class="text-muted">
                    <i class="fas fa-info-circle"></i>
                    Click button override giá ở mỗi item:
                  </small>
                  <table class="table table-sm table-bordered mt-1">
                    <thead>
                      <tr>
                        <th>STT</th>
                        <th>Sản phẩm</th>
                        <th class="text-right">Giá hiện tại</th>
                        <th class="text-center" style="width: 100px">
                          Override
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(it, i) in mainItems" :key="it.id || i">
                        <td>{{ i + 1 }}</td>
                        <td>{{ it.productName }}</td>
                        <td class="text-right">
                          {{ formatPrice(it.productPrice) }}
                        </td>
                        <td class="text-center">
                          <b-button
                            size="sm"
                            variant="primary"
                            @click="openOverrideDialog(it)"
                            v-b-tooltip.hover
                            title="Override giá"
                          >
                            <i class="fas fa-dollar-sign"></i>
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Section: Payment Terms (reuse PaymentTerm disabled) -->
                <template v-if="loadingComponent && paymentTerms.length">
                  <hr
                    class="hr-text"
                    data-content="Điều khoản thanh toán"
                    style="font-weight: 600"
                  />
                  <PaymentTerm
                    :contractData="contractData"
                    :isDisabled="true"
                  />
                </template>

                <!-- Section: Audit log placeholder -->
                <hr
                  class="hr-text"
                  data-content="Audit log"
                  style="font-weight: 600"
                />
                <b-alert variant="info" show>
                  <i class="fas fa-info-circle"></i>
                  TODO Sprint 3: BE chưa có endpoint
                  <code>GET /contract/:id/history</code>. Section này
                  placeholder cho audit log (STATE_TRANSITION, PRICE_OVERRIDE,
                  CANCEL).
                </b-alert>
              </b-container>
            </template>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Dialog: Override price (BootstrapVue modal — match pattern legacy) -->
    <b-modal
      v-model="overrideDialog.show"
      title="Override giá item"
      hide-footer
      no-close-on-backdrop
    >
      <b-form-group>
        <label
          >Sản phẩm: <b>{{ overrideDialog.item.productName }}</b></label
        >
      </b-form-group>
      <b-form-group>
        <label
          >Giá hiện tại:
          <b>{{ formatPrice(overrideDialog.item.productPrice) }}</b></label
        >
      </b-form-group>
      <b-form-group>
        <label>Giá mới <b class="text-danger">*</b>:</label>
        <b-form-input
          v-model.number="overrideDialog.newPrice"
          type="number"
          size="sm"
          placeholder="Nhập giá mới"
        />
        <b-form-invalid-feedback :state="!overrideDialog.errors.newPrice">
          {{ overrideDialog.errors.newPrice }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <label>Lý do (tối thiểu 10 ký tự) <b class="text-danger">*</b>:</label>
        <b-form-textarea
          v-model="overrideDialog.reason"
          size="sm"
          :rows="3"
          :max-rows="5"
          placeholder="Lý do override giá"
        />
        <b-form-invalid-feedback :state="!overrideDialog.errors.reason">
          {{ overrideDialog.errors.reason }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-alert v-if="overrideDialog.errorMessage" variant="danger" show>
        {{ overrideDialog.errorMessage }}
      </b-alert>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          @click="overrideDialog.show = false"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="overrideDialog.submitting"
          @click="submitOverride"
        >
          Cập nhật giá
        </b-button>
      </div>
    </b-modal>

    <!-- Dialog: Cancel contract (BootstrapVue modal) -->
    <b-modal
      v-model="cancelDialog.show"
      title="Hủy hợp đồng"
      hide-footer
      no-close-on-backdrop
    >
      <p class="mb-2">
        HĐ ở trạng thái: <strong>{{ statusLabel(currentStatus) }}</strong>
      </p>
      <b-alert v-if="currentStatus === 'paid'" variant="warning" show>
        HĐ đã <strong>paid</strong> — sẽ set cờ <code>requiresRefund</code> để
        KTT xử lý hoàn tiền thủ công.
      </b-alert>
      <b-alert v-if="currentStatus === 'invoiced'" variant="warning" show>
        HĐ đã <strong>invoiced</strong> — sẽ set cờ
        <code>requiresRefund</code> + <code>requiresInvoiceCancellation</code>.
      </b-alert>
      <b-form-group>
        <label
          >Lý do hủy (tối thiểu 10 ký tự) <b class="text-danger">*</b>:</label
        >
        <b-form-textarea
          v-model="cancelDialog.reason"
          size="sm"
          :rows="3"
          :max-rows="5"
          placeholder="Lý do hủy HĐ"
        />
        <b-form-invalid-feedback :state="!cancelDialog.error">
          {{ cancelDialog.error }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-alert v-if="cancelDialog.errorMessage" variant="danger" show>
        {{ cancelDialog.errorMessage }}
      </b-alert>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          @click="cancelDialog.show = false"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="danger"
          :disabled="cancelDialog.submitting"
          @click="submitCancel"
        >
          Xác nhận hủy
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';

import KTCodePreview from '@/view/content/CodePreview.vue';
import ContractService from '@/core/services/contract.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  CONTRACT_STATUS_LABEL,
  CONTRACT_PRICE_LOCKED_STATUSES,
  CONTRACT_STATE_TRANSITION_MAP,
  CONTRACT_STATUS,
} from '@/core/constants/contract';
import { makeToastSuccess, makeToastFaile, convertPrice } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';
import { ITEM_TYPE } from '@/utils/enum';

// Reuse shared components legacy
import CustomerInfo from '@/view/components/customers/CustomerInfo';
import ContractInfo from '@/view/components/contracts/info';
import EmployeeInfo from '@/view/components/contracts/EmployeeInfo';
import Discount from '@/view/components/contracts/Discount';
import PaymentTerm from '@/view/components/contracts/PaymentTerm';
import ProductTable from '@/view/components/contracts/ProductTable';

// Map status → BootstrapVue badge variant (vì page detail dùng b-badge thay v-chip)
const STATUS_VARIANT_MAP = {
  pendingDeposit: 'warning',
  pendingSignature: 'warning',
  signed: 'info',
  deposited: 'info',
  allocated: 'info',
  paid: 'success',
  invoiced: 'primary',
  delivered: 'primary',
  completed: 'success',
  cancelled: 'secondary',
};

export default {
  name: 'ContractUpsertV2',
  components: {
    KTCodePreview,
    CustomerInfo,
    ContractInfo,
    EmployeeInfo,
    Discount,
    PaymentTerm,
    ProductTable,
  },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      loadingComponent: false,
      contract: null,
      loadError: '',
      // contractData adapter cho shared components legacy
      contractData: {
        id: null,
        storeId: null,
        store: { id: null, name: null },
        contractNumber: null,
        contractDate: null,
        shippingAddress: null,
        customerId: null,
        customerType: 'CUSTOMER',
        customerName: null,
        customerPhone: null,
        customerAddress: null,
        customerGender: null,
        saleId: null,
        saleName: null,
        shipperId: null,
        shipperName: null,
        paymentType: 'DIRECTLY',
        discountType: 'AMOUNT',
        totalDiscountAmount: 0,
        contractDetail: [],
        contractPaymentTerm: [],
      },
      contractEmployeeInfo: {
        saleId: null,
        saleName: '',
        shipperId: null,
        shipperName: '',
        technicalId: null,
        technicalName: '',
        cashierId: null,
        cashierName: '',
        managerId: null,
        managerName: '',
        assistantId: null,
        assistantName: '',
      },
      overrideDialog: {
        show: false,
        item: {},
        newPrice: 0,
        reason: '',
        errors: {},
        errorMessage: '',
        submitting: false,
      },
      cancelDialog: {
        show: false,
        reason: '',
        error: '',
        errorMessage: '',
        submitting: false,
      },
      exportingDocx: false,
    };
  },
  computed: {
    pageTitle() {
      if (!this.contract) return 'Hợp đồng mua bán';
      return `HĐ #${this.contract.contractNumber || this.contract.id}`;
    },
    currentStatus() {
      return (this.contract && this.contract.status) || '';
    },
    items() {
      return this.contractData.contractDetail || [];
    },
    // Override giá chỉ áp cho dòng SP chính — loại dòng quà/bán kèm (đã flatten
    // vào contractDetail để ProductTable hiển thị) khỏi bảng override.
    mainItems() {
      return this.items.filter((it) => it.orderItemType === ITEM_TYPE.MAIN);
    },
    paymentTerms() {
      return this.contractData.contractPaymentTerm || [];
    },
    canOverridePrice() {
      return !CONTRACT_PRICE_LOCKED_STATUSES.includes(this.currentStatus);
    },
    canCancelContract() {
      const allowed = CONTRACT_STATE_TRANSITION_MAP[this.currentStatus] || [];
      return allowed.includes(CONTRACT_STATUS.CANCELLED);
    },
  },
  created() {
    this.loadContract();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hợp đồng', route: '/contracts' },
      { title: 'Chi tiết HĐ' },
    ]);
  },
  methods: {
    checkPermissions,
    async handleExportDocx() {
      if (!this.contract || this.exportingDocx) return;
      this.exportingDocx = true;
      try {
        const response = await ContractService.exportDocx(this.contract.id);
        const cd = response.headers?.['content-disposition'] || '';
        const match = cd.match(/filename="(.+?)"/);
        const filename =
          (match && match[1]) ||
          `HD_${this.contract.contractNumber || this.contract.id}.docx`;
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        makeToastSuccess('Đã tải file hợp đồng');
      } catch (error) {
        const status = error?.response?.status;
        let message = 'Xuất file thất bại';
        if (error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
          } catch (_) {
            // fallback theo status
          }
        }
        if (status === 403) message = 'Bạn không có quyền xuất hợp đồng';
        else if (status === 500)
          message = 'Lỗi server khi xuất file — vui lòng báo IT';
        makeToastFaile(message);
      } finally {
        this.exportingDocx = false;
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return convertPrice(Number(value)) + ' đ';
    },
    formatDate(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '-';
    },
    statusLabel(status) {
      return CONTRACT_STATUS_LABEL[status] || status || '-';
    },
    statusVariant(status) {
      return STATUS_VARIANT_MAP[status] || 'secondary';
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/contracts');
      }
    },
    goToContractRequest(id) {
      if (!id) return;
      this.$router.push({
        name: 'edit-contract-request',
        params: { id },
      });
    },
    mapContractToContractData(data) {
      const items = get(data, 'contractDetail') || get(data, 'items') || [];
      const terms =
        get(data, 'contractPaymentTerm') || get(data, 'paymentTerms') || [];
      this.contractData = {
        id: data.id,
        storeId: data.storeId || null,
        store: { id: data.storeId || null, name: data.store?.name || null },
        contractNumber: data.contractNumber || null,
        contractDate: data.contractDate
          ? moment(data.contractDate).format('DD/MM/YYYY')
          : null,
        shippingAddress: data.shippingAddress || null,
        customerId: data.customerId || null,
        customerType: data.customerType || 'CUSTOMER',
        customerName: data.customerName || null,
        customerPhone: data.customerPhone || null,
        customerAddress: data.customerAddress || null,
        customerGender: data.customerGender || null,
        saleId: data.saleId || null,
        saleName: data.saleName || null,
        shipperId: data.shipperId || null,
        shipperName: data.shipperName || null,
        paymentType: data.paymentType || 'DIRECTLY',
        discountType: data.discountType || 'AMOUNT',
        totalDiscountAmount: data.totalDiscountAmount || 0,
        // BE findOne (nestGiftsIntoContractDetails) lồng SP tặng kèm/bán kèm vào
        // it.gifts/it.attachs → top-level chỉ còn dòng chính. Làm phẳng lại thành
        // dòng riêng (orderItemType GIFT/ATTACH) để ProductTable read-only hiển thị
        // — đồng bộ cách Đề nghị (contract-request/Upsert) map dữ liệu.
        contractDetail: items.flatMap((it) => {
          const mainClientId = this.$uuid.v4();
          const main = {
            id: it.id,
            _clientId: mainClientId,
            productId: it.productId,
            productName: it.productName,
            productCode: it.productCode || '',
            productPrice: Number(it.productPrice) || 0,
            sellingPrice: Number(it.productPrice) || 0,
            sellingPriceMask: Number(it.productPrice) || 0,
            quantity: Number(it.quantity) || 1,
            discountAmount: Number(it.discountAmount) || 0,
            discountType: it.discountType || 'AMOUNT',
            totalAmount:
              Number(it.totalAmountAfterDiscount) ||
              Number(it.productPrice) * Number(it.quantity || 1) -
                Number(it.discountAmount || 0),
            productChassisId: it.productChassisId || null,
            productSerialId: it.productSerialId || null,
            chassisNumber:
              it.chassisNumber ||
              get(it, 'productChassis.chassisNumber') ||
              null,
            engineNumber:
              it.engineNumber || get(it, 'productChassis.engineNumber') || null,
            serialNumber:
              it.serialNumber || get(it, 'productSerial.serialNumber') || null,
            product: {
              type: it.productType || get(it, 'product.type') || null,
            },
            orderItemType: ITEM_TYPE.MAIN,
            productType: 1,
          };
          const gifts = (it.gifts || []).map((g) => ({
            id: g.id,
            _clientId: this.$uuid.v4(),
            belongOrderClientId: mainClientId,
            orderItemType: ITEM_TYPE.GIFT,
            productId: g.productId,
            productName: g.productName,
            productCode: g.productCode || get(g, 'product.code') || '',
            quantity: Number(g.quantity) || 1,
            sellingPrice: 0,
            sellingPriceMask: 0,
            productPrice: 0,
            discountType: 'AMOUNT',
            discountAmount: 0,
            totalAmount: 0,
            productChassisId: g.productChassisId || null,
            productSerialId: g.productSerialId || null,
            chassisNumber: get(g, 'productChassis.chassisNumber') || null,
            engineNumber: get(g, 'productChassis.engineNumber') || null,
            serialNumber: get(g, 'productSerial.serialNumber') || null,
            product: { type: get(g, 'product.type') || g.productType || null },
            productType: 1,
          }));
          const attachs = (it.attachs || []).map((a) => {
            const price = Number(a.productPrice) || 0;
            return {
              id: a.id,
              _clientId: this.$uuid.v4(),
              belongOrderClientId: mainClientId,
              orderItemType: ITEM_TYPE.ATTACH,
              productId: a.productId,
              productName: a.productName,
              productCode: a.productCode || get(a, 'product.code') || '',
              quantity: Number(a.quantity) || 1,
              sellingPrice: price,
              sellingPriceMask: price,
              productPrice: price,
              discountType: a.discountType || 'AMOUNT',
              discountAmount: Number(a.discountAmount) || 0,
              totalAmount: Number(a.totalAmount) || 0,
              productChassisId: a.productChassisId || null,
              productSerialId: a.productSerialId || null,
              chassisNumber: get(a, 'productChassis.chassisNumber') || null,
              engineNumber: get(a, 'productChassis.engineNumber') || null,
              serialNumber: get(a, 'productSerial.serialNumber') || null,
              product: {
                type: get(a, 'product.type') || a.productType || null,
              },
              productType: 1,
            };
          });
          return [main, ...gifts, ...attachs];
        }),
        contractPaymentTerm: terms.map((t) => ({
          id: t.id,
          name: t.name || '',
          totalAmount: Number(t.totalAmount) || 0,
          fromDate: t.fromDate ? moment(t.fromDate).format('DD/MM/YYYY') : null,
          toDate: t.toDate ? moment(t.toDate).format('DD/MM/YYYY') : null,
          description: t.description || '',
          termOrder: Number(t.termOrder) || 1,
        })),
      };
      this.contractEmployeeInfo = {
        saleId: data.saleId || null,
        saleName: data.saleName || '',
        shipperId: data.shipperId || null,
        shipperName: data.shipperName || '',
        technicalId: data.technicalId || null,
        technicalName: data.technicalName || '',
        cashierId: data.cashierId || null,
        cashierName: data.cashierName || '',
        managerId: data.managerId || null,
        managerName: data.managerName || '',
        assistantId: data.assistantId || null,
        assistantName: data.assistantName || '',
      };
    },
    async loadContract() {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await ContractService.getById(this.id);
        const data = get(res, 'data.data') || get(res, 'data');
        this.contract = data || null;
        if (this.contract) {
          this.mapContractToContractData(this.contract);
          this.loadingComponent = true;
        } else {
          this.loadError = 'HĐ không tồn tại';
        }
      } catch (err) {
        this.loadError =
          get(err, 'response.data.message') || 'Không tải được HĐ';
      } finally {
        this.loading = false;
      }
    },
    openOverrideDialog(item) {
      this.overrideDialog = {
        show: true,
        item,
        newPrice: Number(item.productPrice) || 0,
        reason: '',
        errors: {},
        errorMessage: '',
        submitting: false,
      };
    },
    async submitOverride() {
      this.overrideDialog.errors = {};
      this.overrideDialog.errorMessage = '';
      const newPrice = Number(this.overrideDialog.newPrice);
      const reason = (this.overrideDialog.reason || '').trim();
      if (!newPrice || newPrice <= 0) {
        this.$set(this.overrideDialog.errors, 'newPrice', 'Giá phải > 0');
        return;
      }
      if (reason.length < 10) {
        this.$set(
          this.overrideDialog.errors,
          'reason',
          'Lý do tối thiểu 10 ký tự',
        );
        return;
      }
      this.overrideDialog.submitting = true;
      try {
        await ContractService.overrideItemPrice(
          this.contract.id,
          this.overrideDialog.item.id,
          { newPrice, reason },
        );
        makeToastSuccess('Đã cập nhật giá');
        this.overrideDialog.show = false;
        await this.loadContract();
      } catch (err) {
        this.overrideDialog.errorMessage =
          get(err, 'response.data.message') ||
          err.message ||
          'Override giá thất bại';
        makeToastFaile(this.overrideDialog.errorMessage);
      } finally {
        this.overrideDialog.submitting = false;
      }
    },
    async submitCancel() {
      this.cancelDialog.error = '';
      this.cancelDialog.errorMessage = '';
      const reason = (this.cancelDialog.reason || '').trim();
      if (reason.length < 10) {
        this.cancelDialog.error = 'Lý do tối thiểu 10 ký tự';
        return;
      }
      this.cancelDialog.submitting = true;
      try {
        await ContractService.cancel(this.contract.id, reason);
        makeToastSuccess('Đã hủy HĐ');
        this.cancelDialog.show = false;
        await this.loadContract();
      } catch (err) {
        this.cancelDialog.errorMessage =
          get(err, 'response.data.message') || err.message || 'Hủy thất bại';
        makeToastFaile(this.cancelDialog.errorMessage);
      } finally {
        this.cancelDialog.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Style local — KHÔNG inject CSS global, không override Vuetify default
.contract-upsert-v2 {
  .form-group {
    margin-bottom: 1rem;
  }
}
</style>
